import {
  hapticFeedback,
  init,
  initData,
  invoice,
  miniApp,
  openLink,
  retrieveLaunchParams,
  retrieveRawInitData,
  swipeBehavior,
  viewport,
} from '@tma.js/sdk'
import type { HapticType, InvoicePayResult, PlatformAdapter, PlatformInitResult } from './adapter'

/**
 * Telegram platform adapter, backed by the bundled `@tma.js/sdk` (no
 * external script). We pulled the SDK in-tree specifically so we don't
 * depend on `telegram.org/js/telegram-web-app.js` — that domain is
 * intermittently blocked by Russian ISPs, which left the previous
 * implementation showing a "blocked, use VPN" screen for users who
 * legitimately opened the mini-app from Telegram.
 *
 * The native bridge (`window.TelegramWebviewProxy.postEvent`) is injected
 * by the TG client itself, so as long as the user IS in TG the SDK can
 * talk to the client without any network round-trip.
 */
export class TelegramPlatformAdapter implements PlatformAdapter {
  readonly platform = 'telegram' as const

  async init(): Promise<PlatformInitResult> {
    // Idempotent — safe to call from detection flow even if something else
    // already initialised the SDK.
    init()
    miniApp.mount()
    // viewport.mount waits for a "viewport-stable" event from the TG
    // client. On some clients (web TG, certain mobile versions) that
    // event never fires and the promise hangs forever — users see the
    // splash screen for minutes. Race against a 1.5s timeout and proceed
    // anyway; viewport.expand() below works fine without "stable".
    await Promise.race([
      viewport.mount(),
      new Promise<void>((resolve) => setTimeout(resolve, 1500)),
    ]).catch(() => {})
    miniApp.ready()
    try {
      if (!viewport.isExpanded()) viewport.expand()
    } catch {
      // expand can throw if viewport never mounted — non-fatal.
    }
    // Disable vertical edge-swipe (downward swipe-to-close). This is the
    // most common "I accidentally closed the app" gesture on iOS — and the
    // mini-app already has a native close button in the title bar plus the
    // BackButton wired up, so the gesture has no real upside here.
    if (swipeBehavior.isSupported()) {
      swipeBehavior.mount()
      swipeBehavior.disableVertical()
    }

    const launch = retrieveLaunchParams()
    const u = launch.tgWebAppData?.user
    return {
      initDataRaw: retrieveRawInitData() ?? '',
      user: {
        id: u ? String(u.id) : 'unknown',
        firstName: u?.first_name,
        lastName: u?.last_name,
        username: u?.username,
        languageCode: u?.language_code,
      },
      theme: { colorScheme: 'light' },
    }
  }

  getLanguageHint(): string {
    return initData.user()?.language_code ?? 'ru'
  }

  async share(payload: { url: string; text: string }): Promise<void> {
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(payload.url)}&text=${encodeURIComponent(payload.text)}`
    openLink(shareUrl)
  }

  async openLink(url: string): Promise<void> {
    openLink(url)
  }

  async openInvoice(url: string): Promise<InvoicePayResult> {
    if (!invoice.openUrl.isAvailable()) {
      // Older TG client — fall back to opening the link and letting the
      // user complete the flow externally. The FE keeps polling
      // entitlements as before.
      openLink(url)
      return 'pending'
    }
    try {
      const status = await invoice.openUrl(url)
      return (status as InvoicePayResult) ?? 'pending'
    } catch {
      return 'failed'
    }
  }

  hapticFeedback(type: HapticType): void {
    if (!hapticFeedback.isSupported()) return
    if (type === 'success' || type === 'warning' || type === 'error') {
      hapticFeedback.notificationOccurred(type)
    } else {
      hapticFeedback.impactOccurred(type)
    }
  }

  async showAlert(text: string): Promise<void> {
    alert(text)
  }

  closeApp(): void {
    miniApp.close()
  }

  getStartParam(): string | null {
    return initData.startParam() ?? null
  }
}
